import { PERMISSIONS } from "./permissions";

/**
 * Maps each of the 16 Settings permission labels to the granular
 * PermissionKey[] values it controls at runtime.
 */
export const SETTINGS_PERM_TO_KEYS: Record<string, string[]> = {
  // Jobs & Projects
  "View Jobs":                    ["jobs_view"],
  "Create Jobs":                  ["jobs_create"],
  "Edit Jobs":                    ["jobs_edit", "jobs_approve"],
  "Delete Jobs":                  ["jobs_delete"],
  "Approve QA":                   ["qa_approve", "qa_view"],
  "Close Jobs":                   ["jobs_approve", "installation_approve"],
  "View Job Financials":          ["accounting_view"],

  // Scheduling & Dispatch
  "View Scheduling":              ["scheduling_view"],
  "Manage Scheduling":            ["scheduling_create", "scheduling_edit", "scheduling_approve"],
  "Assign Crew":                  ["scheduling_edit", "installation_edit"],
  "Reschedule Jobs":              ["scheduling_edit"],

  // Issues
  "View Issues":                  ["issues_view"],
  "Manage Issues":                ["issues_create", "issues_edit", "issues_approve"],
  "Resolve Issues":               ["issues_approve", "issues_edit"],

  // Procurement & Warehouse
  "View Ordering":                ["ordering_view"],
  "Create Purchase Orders":       ["ordering_create", "ordering_edit"],
  "Approve Purchase Orders":      ["ordering_approve"],
  "View Inventory":               ["inventory_view", "warehouse_view"],
  "Manage Inventory":             [
    "inventory_create", "inventory_edit", "inventory_delete",
    "warehouse_create", "warehouse_edit", "warehouse_delete",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
  ],
  "Mark Materials Ready":         ["warehouse_edit", "warehouse_approve"],

  // Execution
  "View Execution":               ["installation_view"],
  "Manage Execution":             ["installation_edit", "installation_create"],
  "Mark Phase Complete":          ["installation_approve"],

  // Network / Contractors
  "Manage Contractors":           ["contractors_view", "contractors_create", "contractors_edit", "contractors_approve"],
  "Approve Contractor Onboarding": ["contractors_approve", "contractors_create"],
  "Manage Installer Offers":      ["scheduling_create", "scheduling_edit"],

  // Customers & Pipeline
  "View Customers":               ["customers_view"],
  "View Pipeline":                ["leads_view"],
  "Manage Pipeline":              ["leads_create", "leads_edit", "leads_approve"],
  "Manage Lead Sources":          ["leads_view", "leads_create", "leads_edit"],

  // Financials
  "View Financials":              ["accounting_view", "accounting_export"],
  "Manage Financials":            ["accounting_create", "accounting_edit", "accounting_delete"],
  "View Commissions":             ["accounting_view", "reports_view"],
  "Manage Commissions":           ["accounting_edit", "accounting_create"],
  "Approve Payments":             ["accounting_approve"],

  // Reports
  "View Reports":                 ["reports_view", "reports_export"],
  "Export Reports":               ["reports_export"],

  // Users & Settings
  "Manage Users":                 ["settings_view", "settings_edit"],
  "Manage Settings":              ["settings_view", "settings_edit", "admin_view", "admin_edit"],
  "Manage Roles & Permissions":   ["settings_edit", "admin_edit"],
};

/**
 * All PermissionKeys covered by any Settings label.
 * Keys NOT in this set are provided via the static ROLE_PERMISSIONS fallback.
 */
export const COVERED_KEYS = new Set<string>(
  Object.values(SETTINGS_PERM_TO_KEYS).flat(),
);

/**
 * Expand a list of Settings labels into a deduplicated flat PermissionKey[].
 */
export function expandSettingsLabels(labels: string[]): string[] {
  const keys: string[] = [];
  for (const label of labels) {
    const mapped = SETTINGS_PERM_TO_KEYS[label];
    if (mapped) keys.push(...mapped);
  }
  return [...new Set(keys)];
}

/**
 * Check whether a route pattern (e.g. "/jobs/:id") matches a live pathname.
 */
export function routeMatchesPattern(pattern: string, pathname: string): boolean {
  const regexStr = "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$";
  return new RegExp(regexStr).test(pathname);
}

/**
 * Direct route → permission-key aliases for nav paths that are not
 * naturally covered by a parent path in the PERMISSIONS registry.
 */
export const ROUTE_PERMISSION_ALIASES: Record<string, string> = {
  "/lead-sources": "leads_view",
  "/lead-integrity": "leads_view",
  "/growth-channels": "leads_view",
  "/marketing-attribution": "reports_view",
  "/follow-ups": "leads_view",
  "/appointments": "leads_view",
  "/appointment-results": "leads_view",
  "/rep-scorecards": "reports_view",
  "/commissions": "accounting_view",
  "/scheduling-dispatch": "scheduling_view",
  "/dispatch-intelligence": "scheduling_view",
  "/installer-dashboard": "jobs_view",
  "/pipeline-intelligence": "reports_view",
  "/follow-up-engine": "automations_view",
  "/revenue-intelligence": "accounting_view",
  "/earnings-intelligence": "accounting_view",
  "/command-dashboard": "reports_view",
  "/admin/installer-organizations": "contractors_view",
  "/admin/compliance-review": "admin_view",
  "/closeout": "jobs_view",
  "/contractor-intake": "contractors_view",
  "/contractor-onboarding": "contractors_create",
  "/demo-lab": "admin_view",
  "/friction-log": "admin_view",
  "/navigation-settings": "settings_edit",
  "/control-plane": "admin_view",
  "/offerings": "catalog_view",
  "/product-registry": "catalog_view",
  "/checklist-templates": "admin_view",
  "/resolve": "issues_view",
  "/installer-offers": "scheduling_view",
  "/execution": "jobs_view",
  "/projects": "jobs_view",
  "/service-tickets": "issues_view",
  "/portals": "admin_view",
  "/intelligence": "reports_view",
  "/alerts": "reports_view",
  "/disposition-report": "reports_view",
  "/qa-form": "qa_view",
  "/messages": "messages_view",
  "/finance/pay-runs": "accounting_view",
  "/finance/payouts": "accounting_view",
  "/finance/approvals": "accounting_view",
  "/finance/collections": "accounting_view",
  "/finance/bills": "accounting_view",
  "/finance/purchase-orders": "accounting_view",
  "/finance/payroll": "accounting_view",
  "/finance/close": "accounting_view",
};

/**
 * Return true if the user's hasPermission function grants access to pathname.
 * Checks the PERMISSIONS registry for any key whose .route matches.
 * The root "/" is always permitted.
 *
 * Parent-path rule: if the user has a "view" permission whose route is a
 * direct parent of the pathname (e.g. jobs_view → "/jobs" covers "/jobs/:id"),
 * access is granted. This prevents "Edit Jobs" being disabled from blocking
 * the job detail page when "View Jobs" is still enabled.
 */
export function isPermittedRoute(
  hasPermission: (key: string) => boolean,
  pathname: string,
): boolean {
  if (pathname === "/" || pathname === "") return true;

  const aliasKey = ROUTE_PERMISSION_ALIASES[pathname];
  if (aliasKey && hasPermission(aliasKey)) return true;

  return Object.values(PERMISSIONS).some(
    (def) =>
      def.route != null &&
      hasPermission(def.key) &&
      (
        routeMatchesPattern(def.route, pathname) ||
        // Parent-path coverage: /jobs covers /jobs/:id, /jobs/:id/tab, etc.
        pathname.startsWith(def.route.replace(/:[^/]+.*$/, "").replace(/\/$/, "") + "/")
      ),
  );
}
